/**
 * Authentication-specific React hooks
 * Auth state management, permissions, session handling için hooks
 */

import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { authService } from '../services';
import { UserProfile, UserRole } from '../types/api.types';

/**
 * Auth state interface
 */
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;
  token: string | null;
  error: Error | null;
}

/**
 * Permission check result
 */
export interface PermissionCheck {
  hasPermission: boolean;
  missingPermissions: string[];
}

/**
 * Auth hook options
 */
export interface UseAuthOptions {
  requireAuth?: boolean;
  redirectTo?: string;
  onUnauthenticated?: () => void;
  onAuthenticated?: (user: UserProfile) => void;
}

/**
 * Main authentication hook
 */
export function useAuth(options?: UseAuthOptions) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    token: null,
    error: null
  });

  const queryClient = useQueryClient();

  // Auth state'i güncelle
  const updateAuthState = useCallback(() => {
    const isAuthenticated = authService.isAuthenticated();
    const user = authService.getCurrentUser();
    const token = authService.getAccessToken();

    setState({
      isAuthenticated,
      isLoading: false,
      user,
      token,
      error: null
    });

    // Callback'leri çağır
    if (isAuthenticated && user && options?.onAuthenticated) {
      options.onAuthenticated(user);
    }

    if (!isAuthenticated && options?.onUnauthenticated) {
      options.onUnauthenticated();
    }

    // Redirect logic
    if (options?.requireAuth && !isAuthenticated && options.redirectTo) {
      globalThis.location.href = options.redirectTo;
    }
  }, [options]);

  // Initial load ve auth state changes
  useEffect(() => {
    updateAuthState();

    // Auth state değişikliklerini dinle
    const unsubscribeAuth = authService.onAuthChange((isAuthenticated) => {
      setState(prev => ({
        ...prev,
        isAuthenticated,
        isLoading: false
      }));
    });

    // User değişikliklerini dinle
    const unsubscribeUser = authService.onUserChange((user) => {
      setState(prev => ({
        ...prev,
        user,
        isLoading: false
      }));
    });

    // Session timeout'u dinle
    const unsubscribeTimeout = authService.onSessionTimeout(() => {
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: new Error('Session expired')
      });
      
      if (options?.redirectTo) {
        globalThis.location.href = options.redirectTo;
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeUser();
      unsubscribeTimeout();
    };
  }, [updateAuthState, options]);

  // Login function
  const login = useCallback(async (credentials: any) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const result = await authService.login(credentials);
      updateAuthState();
      return result;
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error as Error 
      }));
      throw error;
    }
  }, [updateAuthState]);

  // Logout function
  const logout = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      await authService.logout();
      
      // Query cache'ini temizle
      queryClient.clear();
      
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
        error: null
      });
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: error as Error 
      }));
      throw error;
    }
  }, [queryClient]);

  // Refresh session
  const refreshSession = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const isValid = await authService.validateSession();
      
      if (!isValid) {
        throw new Error('Session is not valid');
      }
      
      updateAuthState();
      return true;
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        isLoading: false,
        isAuthenticated: false,
        error: error as Error 
      }));
      return false;
    }
  }, [updateAuthState]);

  return {
    // State
    ...state,
    
    // Actions
    login,
    logout,
    refreshSession,
    
    // Convenience
    isAdmin: state.user?.role === 'admin' || state.user?.role === 'superadmin',
    isSuperAdmin: state.user?.role === 'superadmin',
    isManager: state.user?.role === 'manager',
    isOperator: state.user?.role === 'operator',
    isViewer: state.user?.role === 'viewer',
    
    // User info shortcuts
    userId: state.user?.id,
    userEmail: state.user?.email,
    userName: state.user?.fullName || `${state.user?.firstName} ${state.user?.lastName}`,
    userRole: state.user?.role,
    userTenantId: state.user?.tenantId,
    
    // Update functions
    updateProfile: authService.updateProfile.bind(authService),
    changePassword: authService.changePassword.bind(authService)
  };
}

/**
 * Protected route hook - authentication required
 */
export function useProtectedRoute(redirectTo: string = '/login') {
  const auth = useAuth({
    requireAuth: true,
    redirectTo,
    onUnauthenticated: () => {
      console.log('User not authenticated, redirecting to login');
    }
  });

  return auth;
}

/**
 * Guest route hook - only for unauthenticated users
 */
export function useGuestRoute(redirectTo: string = '/dashboard') {
  const auth = useAuth({
    onAuthenticated: () => {
      globalThis.location.href = redirectTo;
    }
  });

  return {
    ...auth,
    // Override login to redirect after success
    login: async (credentials: any) => {
      const result = await auth.login(credentials);
      globalThis.location.href = redirectTo;
      return result;
    }
  };
}

/**
 * Permission checking hook
 */
export function usePermissions() {
  const { user } = useAuth();
  
  const checkPermission = useCallback((permission: string | string[]): PermissionCheck => {
    const requiredPermissions = Array.isArray(permission) ? permission : [permission];
    const userPermissions = user?.permissions || [];
    
    const missingPermissions = requiredPermissions.filter(
      perm => !userPermissions.includes(perm)
    );
    
    return {
      hasPermission: missingPermissions.length === 0,
      missingPermissions
    };
  }, [user]);
  
  const checkAnyPermission = useCallback((permissions: string[]): boolean => {
    const userPermissions = user?.permissions || [];
    return permissions.some(perm => userPermissions.includes(perm));
  }, [user]);
  
  const checkAllPermissions = useCallback((permissions: string[]): boolean => {
    const userPermissions = user?.permissions || [];
    return permissions.every(perm => userPermissions.includes(perm));
  }, [user]);
  
  const hasRole = useCallback((role: UserRole | UserRole[]): boolean => {
    const requiredRoles = Array.isArray(role) ? role : [role];
    return requiredRoles.includes(user?.role as UserRole);
  }, [user]);
  
  return {
    checkPermission,
    checkAnyPermission,
    checkAllPermissions,
    hasRole,
    permissions: user?.permissions || [],
    role: user?.role
  };
}

/**
 * Role-based access control hook
 */
export function useRBAC() {
  const { user } = useAuth();
  const permissions = usePermissions();
  
  const can = useCallback((action: string, resource?: string): boolean => {
    if (!user) return false;
    
    // Super admin her şeyi yapabilir
    if (user.role === 'superadmin') return true;
    
    // Admin check
    if (user.role === 'admin') {
      // Admin'lerin yapamayacağı bazı actions
      const adminRestrictedActions = ['delete-tenant', 'manage-superadmin'];
      if (adminRestrictedActions.includes(action)) return false;
      return true;
    }
    
    // Resource-based permission check
    if (resource) {
      const permission = `${action}:${resource}`;
      return permissions.checkPermission(permission).hasPermission;
    }
    
    // Action-based permission check
    return permissions.checkPermission(action).hasPermission;
  }, [user, permissions]);
  
  const cannot = useCallback((action: string, resource?: string): boolean => {
    return !can(action, resource);
  }, [can]);
  
  return {
    can,
    cannot,
    isSuperAdmin: user?.role === 'superadmin',
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager',
    isOperator: user?.role === 'operator',
    isViewer: user?.role === 'viewer'
  };
}

/**
 * Tenant context hook (multi-tenant uygulamalar için)
 */
export function useTenant() {
  const { user, userTenantId } = useAuth();
  
  const isMultiTenant = useCallback((): boolean => {
    return !!userTenantId;
  }, [userTenantId]);
  
  const getTenantId = useCallback((): string | undefined => {
    return userTenantId;
  }, [userTenantId]);
  
  const switchTenant = useCallback(async (tenantId: string): Promise<boolean> => {
    if (!user) return false;
    
    try {
      // Bu fonksiyon backend'de implement edilmeli
      // Örnek: await authService.switchTenant(tenantId);
      console.log(`Switching to tenant: ${tenantId}`);
      return true;
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      return false;
    }
  }, [user]);
  
  return {
    tenantId: userTenantId,
    isMultiTenant,
    getTenantId,
    switchTenant,
    hasTenantAccess: !!userTenantId
  };
}

/**
 * Session management hook
 */
export function useSession() {
  const auth = useAuth();
  
  const [lastActivity, setLastActivity] = useState<Date>(new Date());
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout>();
  
  // Activity tracking
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(new Date());
    };
    
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    for (const event of events) {
        globalThis.addEventListener(event, updateActivity);
    }
    
    return () => {
      for (const event of events) {
        globalThis.removeEventListener(event, updateActivity);
      }
    };
  }, []);
  
  // Inactivity timeout
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    
    const checkInactivity = () => {
      const now = new Date();
      const diff = now.getTime() - lastActivity.getTime();
      const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 dakika
      
      if (diff > INACTIVITY_LIMIT) {
        auth.logout();
      }
    };
    
    const timer = setInterval(checkInactivity, 60 * 1000); // Her dakika kontrol et
    setInactivityTimer(timer);
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [auth.isAuthenticated, lastActivity, auth.logout]);
  
  const extendSession = useCallback(async (): Promise<boolean> => {
    if (!auth.token) return false;
    
    try {
      return await auth.refreshSession();
    } catch (error) {
      console.error('Failed to extend session:', error);
      return false;
    }
  }, [auth.token, auth.refreshSession]);
  
  const getSessionTimeLeft = useCallback((): number => {
    if (!auth.token) return 0;
    
    // Token expiry kontrolü (örnek implementasyon)
    // Gerçek implementasyonda JWT decode edilip expiry time alınmalı
    const TOKEN_LIFETIME = 60 * 60 * 1000; // 1 saat (örnek)
    return TOKEN_LIFETIME;
  }, [auth.token]);
  
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer) {
      clearInterval(inactivityTimer);
    }
    
    setLastActivity(new Date());
  }, [inactivityTimer]);
  
  return {
    lastActivity,
    extendSession,
    getSessionTimeLeft,
    resetInactivityTimer,
    isSessionValid: auth.isAuthenticated
  };
}

/**
 * Auth status polling hook (real-time auth status için)
 */
export function useAuthPolling(interval: number = 30000) {
  const auth = useAuth();
  const [isPolling, setIsPolling] = useState(false);
  
  useEffect(() => {
    if (!auth.isAuthenticated || !interval) return;
    
    setIsPolling(true);
    
    const poll = async () => {
      try {
        await auth.refreshSession();
      } catch (error) {
        console.error('Auth polling failed:', error);
        setIsPolling(false);
      }
    };
    
    const pollInterval = setInterval(poll, interval);
    
    return () => {
      clearInterval(pollInterval);
      setIsPolling(false);
    };
  }, [auth.isAuthenticated, interval, auth.refreshSession]);
  
  return {
    isPolling,
    startPolling: () => setIsPolling(true),
    stopPolling: () => setIsPolling(false)
  };
}

/**
 * Login form hook (form handling için)
 */
export function useLoginForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();
  
  const handleSubmit = useCallback(async (credentials: any) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await login(credentials);
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [login]);
  
  return {
    handleSubmit,
    isSubmitting,
    error,
    clearError: () => setError(null)
  };
}

/**
 * Logout with confirmation hook
 */
export function useLogoutWithConfirmation() {
  const { logout } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  const handleLogout = useCallback(async () => {
    setIsLoggingOut(true);
    
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
      setShowConfirm(false);
    }
  }, [logout]);
  
  const confirmLogout = useCallback(() => {
    setShowConfirm(true);
  }, []);
  
  const cancelLogout = useCallback(() => {
    setShowConfirm(false);
  }, []);
  
  return {
    handleLogout,
    confirmLogout,
    cancelLogout,
    showConfirm,
    isLoggingOut
  };
}

/**
 * Auto-login hook (remember me özelliği için)
 */
export function useAutoLogin() {
  const [isAttemptingAutoLogin, setIsAttemptingAutoLogin] = useState(true);
  const { refreshSession } = useAuth();
  
  useEffect(() => {
    const attemptAutoLogin = async () => {
      try {
        await refreshSession();
      } catch (error) {
        console.log('Auto login failed:', error);
      } finally {
        setIsAttemptingAutoLogin(false);
      }
    };
    
    attemptAutoLogin();
  }, [refreshSession]);
  
  return {
    isAttemptingAutoLogin
  };
}

// ==================== DEFAULT EXPORT ====================

/**
 * Tüm auth hook'larını içeren ana nesne
 */
const authHooks = {
  useAuth,
  useProtectedRoute,
  useGuestRoute,
  usePermissions,
  useRBAC,
  useTenant,
  useSession,
  useAuthPolling,
  useLoginForm,
  useLogoutWithConfirmation,
  useAutoLogin
};

export default authHooks;